import { Component } from '@theme/component';

class ShowMoreTextComponent extends Component {
  requiredRefs = ['showMoreButton', 'showMoreContent'];

  #expanded = false;
  #collapsedHeight = 0;
  #animation;
  #animationSpeed = 300;

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.#syncState);
    this.#syncState();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#syncState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#syncState);
  }

  get #configuredCollapsedHeight() {
    const collapsedHeightMobile = Number.parseInt(this.dataset.collapsedHeightMobile ?? '', 10);
    const collapsedHeight = Number.parseInt(this.dataset.collapsedHeight ?? '', 10);

    if (window.matchMedia('(max-width: 749px)').matches) {
      if (Number.isFinite(collapsedHeightMobile) && collapsedHeightMobile > 0) {
        return collapsedHeightMobile;
      }
    }

    if (!Number.isFinite(collapsedHeight) || collapsedHeight <= 0) return 0;

    return collapsedHeight;
  }

  #syncState = () => {
    const { showMoreButton, showMoreContent } = this.refs;
    const collapsedHeight = this.#configuredCollapsedHeight;
    const hasOverflow = collapsedHeight > 0 && showMoreContent.scrollHeight > collapsedHeight + 1;

    showMoreButton.hidden = !hasOverflow;

    if (!hasOverflow) {
      this.#expanded = false;
      this.dataset.expanded = 'false';
      showMoreButton.setAttribute('aria-expanded', 'false');
      showMoreContent.style.removeProperty('height');
      showMoreContent.style.removeProperty('max-height');
      showMoreContent.style.overflow = '';
      return;
    }

    this.#collapsedHeight = collapsedHeight;

    if (this.#expanded) {
      showMoreContent.style.removeProperty('max-height');
      showMoreContent.style.overflow = '';
    } else {
      showMoreContent.style.maxHeight = `${this.#collapsedHeight}px`;
      showMoreContent.style.overflow = 'hidden';
    }
  };

  #expand = () => {
    const { showMoreContent } = this.refs;

    this.#collapsedHeight = this.#configuredCollapsedHeight;
    const startHeight = showMoreContent.offsetHeight;

    showMoreContent.style.removeProperty('max-height');

    return {
      startHeight,
      endHeight: showMoreContent.scrollHeight,
    };
  };

  #collapse = () => {
    const { showMoreContent } = this.refs;

    return {
      startHeight: showMoreContent.offsetHeight,
      endHeight: this.#collapsedHeight,
    };
  };

  #animateHeight = (startHeight, endHeight) => {
    const { showMoreContent } = this.refs;

    showMoreContent.style.overflow = 'hidden';
    this.#animation?.cancel();

    this.#animation = showMoreContent.animate(
      {
        height: [`${startHeight}px`, `${endHeight}px`],
      },
      {
        duration: this.#animationSpeed,
        easing: 'ease-in-out',
      }
    );

    this.#animation.onfinish = () => this.#onAnimationFinish();
  };

  #onAnimationFinish = () => {
    const { showMoreContent } = this.refs;

    if (this.#expanded) {
      showMoreContent.style.maxHeight = `${this.#collapsedHeight}px`;
      showMoreContent.style.overflow = 'hidden';
    } else {
      showMoreContent.style.removeProperty('max-height');
      showMoreContent.style.overflow = '';
    }

    showMoreContent.style.removeProperty('height');
    this.#expanded = !this.#expanded;
  };

  toggle = (event) => {
    event.preventDefault();

    if (this.refs.showMoreButton.hidden) return;

    const { startHeight, endHeight } = !this.#expanded ? this.#expand() : this.#collapse();

    this.dataset.expanded = this.#expanded ? 'false' : 'true';
    this.refs.showMoreButton.setAttribute('aria-expanded', this.dataset.expanded);

    this.#animateHeight(startHeight, endHeight);
  };
}

if (!customElements.get('show-more-text-component')) {
  customElements.define('show-more-text-component', ShowMoreTextComponent);
}